import React, { useState } from "react";

import {
  Colors,
  Typography,
  Iconography,
  Components,
  Effects,
} from "./sections";

// Import design system styles (which includes all Excalidraw CSS)
import "./ds.scss";

type Section =
  | "colors"
  | "typography"
  | "iconography"
  | "components"
  | "effects";

export const DesignSystem: React.FC = () => {
  const [activeSection, setActiveSection] = useState<Section>(() => {
    // Restore active section from localStorage or default to "colors"
    const saved = localStorage.getItem("ds-active-section");
    return (saved as Section) || "colors";
  });
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Restore dark mode from localStorage or default to false
    const saved = localStorage.getItem("ds-dark-mode");
    return saved === "true";
  });

  // Enable scrolling by overriding main app's overflow:hidden
  React.useEffect(() => {
    document.body.classList.add("design-system-active");
    document.documentElement.style.overflow = "unset";
    document.body.style.overflow = "unset";
    document.body.style.height = "unset";
    document.documentElement.style.height = "unset";

    // Restore scroll position
    const savedScrollY = localStorage.getItem("ds-scroll-position");
    if (savedScrollY) {
      window.scrollTo(0, parseInt(savedScrollY, 10));
    }

    // Save scroll position on scroll
    const handleScroll = () => {
      localStorage.setItem("ds-scroll-position", window.scrollY.toString());
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      document.body.classList.remove("design-system-active");
      document.documentElement.style.overflow = "";
      document.body.style.overflow = "";
      document.body.style.height = "";
      document.documentElement.style.height = "";
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Save active section when it changes
  React.useEffect(() => {
    localStorage.setItem("ds-active-section", activeSection);
  }, [activeSection]);

  // Save dark mode when it changes
  React.useEffect(() => {
    localStorage.setItem("ds-dark-mode", isDarkMode.toString());
  }, [isDarkMode]);

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  const sections = [
    { id: "colors" as const, label: "Colors", icon: "🎨" },
    { id: "typography" as const, label: "Typography", icon: "📝" },
    { id: "iconography" as const, label: "Iconography", icon: "🎯" },
    { id: "components" as const, label: "Components", icon: "🧩" },
    { id: "effects" as const, label: "Effects", icon: "✨" },
  ];

  return (
    <div className={`design-system ${isDarkMode ? "dark" : ""}`}>
      <div className={`excalidraw ${isDarkMode ? "theme--dark" : ""}`}>
        {/* Sidebar */}
        <nav className="ds-sidebar">
          <div className="ds-sidebar-header">
            <h1>🎨 LoFi Design System</h1>
            <p className="ds-subtitle">Visual design language and components</p>
          </div>

          <div className="ds-sidebar-nav">
            {sections.map((section) => (
              <button
                key={section.id}
                className={`ds-nav-item ${
                  activeSection === section.id ? "active" : ""
                }`}
                onClick={() => setActiveSection(section.id)}
                data-section={section.id}
              >
                <span className="ds-nav-icon">{section.icon}</span>
                {section.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Main Content */}
        <main className="ds-main-content">
          <div className="ds-theme-toggle">
            <button onClick={toggleTheme}>
              {isDarkMode ? "☀️" : "🌙"} Toggle {isDarkMode ? "Light" : "Dark"}{" "}
              Mode
            </button>
          </div>

          <div className="ds-content">
            {activeSection === "colors" && <Colors />}
            {activeSection === "typography" && <Typography />}
            {activeSection === "iconography" && <Iconography />}
            {activeSection === "components" && <Components />}
            {activeSection === "effects" && <Effects />}
          </div>
        </main>
      </div>
    </div>
  );
};
